import React, { useEffect, useState } from "react";
import { Layout, Drawer, Menu, Button, Typography, Divider } from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  MoonOutlined,
  SunOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { MenuItems } from "../../utils/getItemsMenu";
import { Switch } from "antd";
import { renderVersion } from "../../utils/renderVersion";
import { useMenuItems } from "../hooks/useMenuItems";
import "./index.css";
import { useAppVersionContext } from "../../AppVersionContext";

const { Header: AntHeader, Content } = Layout;

const AppLayout = ({ children, header, darkMode, setDarkMode }) => {
  const { loggedUser, logout, setOpenMenu, openMenu, cenope } = useAuth();

  const { backendVerfro, backendVerbac } = useAppVersionContext();

  const location = useLocation();
  const navigate = useNavigate();

  const [selectedMenuKey, setSelectedMenuKey] = useState(null);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const { items: menuItems } = useMenuItems(true, true);

  const enterLogoutLoading = () => {
    setLogoutLoading(true);
    setTimeout(() => {
      setLogoutLoading(false);
      setOpenMenu(false);
      logout();
    }, 2000);
  };

  // Determina si la ruta actual permite swipe
  const isSwipeAllowed = React.useMemo(() => {
    // Solo permitir swipe si la ruta coincide EXACTAMENTE con alguna ruta "padre" del menú
    return MenuItems.some((item) => item.path === location.pathname);
  }, [location.pathname]);

  // Manejadores de swipe (solo se usan si isSwipeAllowed es true)
  const handleLeftEdgeTouchStart = (e) => {
    window.ontouchstart = e.touches[0].clientX;
  };
  const handleLeftEdgeTouchEnd = (e) => {
    if (!isSwipeAllowed) return;
    const touchEndX = e.changedTouches[0].clientX;
    if (window.ontouchstart < 40 && touchEndX - window.ontouchstart > 30) {
      setOpenMenu(true);
    }
  };

  const handleDrawerSwipe = (e) => {
    if (!isSwipeAllowed) return;
    if (!window._drawerTouchStartX) return;
    const touchEndX = e.changedTouches[0].clientX;
    if (window._drawerTouchStartX - touchEndX > 30) {
      setOpenMenu(false);
    }
    window._drawerTouchStartX = null;
  };

  // Sincroniza título del header y clave seleccionada con la ruta actual
  useEffect(() => {
    const currentPath = location.pathname === "/" ? "/home" : location.pathname;

    const currentItem = MenuItems.find((item) => item.path === currentPath);

    if (currentItem) {
      setSelectedMenuKey(currentItem.key);
    } else {
      setSelectedMenuKey(null);
    }
  }, [location.pathname]);

  return (
    <div id="mobile-root">
      <Layout style={{ height: "100dvh" }}>
        {/* Zona para abrir Drawer con swipe */}
        {isSwipeAllowed && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "40px",
              height: "100dvh",
              zIndex: 1,
            }}
            onTouchStart={handleLeftEdgeTouchStart}
            onTouchEnd={handleLeftEdgeTouchEnd}
          />
        )}

        {/* Header segun escenario */}
        {header && (
          <AntHeader
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingInline: "8px",
              zIndex: 100,
              top: 0,
              position: "sticky",
            }}
          >
            {header}
          </AntHeader>
        )}

        {/* Drawer */}
        <Drawer
          getContainer={false}
          motion={false}
          title={
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Menú</span>
              <Switch
                checked={darkMode}
                onChange={() => setDarkMode(!darkMode)}
                checkedChildren={<MoonOutlined />}
                unCheckedChildren={<SunOutlined />}
              />
            </div>
          }
          placement="left"
          onClose={() => setOpenMenu(false)}
          open={openMenu}
          styles={{
            body: { padding: 0 },
          }}
        >
          <div
            style={{
              height: 60,
              padding: 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography.Title level={4} style={{ margin: 0 }}>
              <UserOutlined /> {loggedUser?.nombre}
            </Typography.Title>
          </div>

          <Divider style={{ margin: 0 }} />

          {isSwipeAllowed && (
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: "40px",
                height: "100%",
                zIndex: 2, // debe estar por encima del fondo, pero por debajo del contenido interactivo
              }}
              onTouchStart={(e) => {
                window._drawerTouchStartX = e.touches[0].clientX;
              }}
              onTouchEnd={handleDrawerSwipe}
            />
          )}

          {/* Menú principal */}
          <Menu
            theme={darkMode}
            mode="vertical"
            items={menuItems}
            onClick={({ key }) => {
              const item = MenuItems.find((i) => i.key === key);
              if (item?.path) {
                setOpenMenu(false);
                navigate(item.path);
              }
            }}
            selectedKeys={selectedMenuKey ? [selectedMenuKey] : []}
            className="custom-menu"
            style={{ position: "relative", zIndex: 1 }}
          />

          <div className="version-wrapper">
            <Typography.Text type="secondary">
              {backendVerfro} | API {backendVerbac} | {renderVersion(cenope)}
            </Typography.Text>
          </div>

          <div className="drawer-wrapper-logout">
            <div
              style={{
                textAlign: "center",
                fontSize: "12px",
                padding: "12px",
              }}
            >
              <Typography.Text type="secondary">
                © {new Date().getFullYear()} Innercor S.R.L
              </Typography.Text>
            </div>
            <Button
              type="primary"
              size="large"
              danger
              loading={logoutLoading}
              onClick={enterLogoutLoading}
              style={{ width: "100%", fontSize: "17px" }}
              icon={<LogoutOutlined rotate={180} />}
            >
              Cerrar Sesión
            </Button>
          </div>
        </Drawer>

        {/* Contenido */}

        <Content
          style={{
            padding: 0,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {children}
        </Content>
      </Layout>
    </div>
  );
};

export default AppLayout;
